import { ICell, ICellConfig, ILayout, ILayoutConfig, LayoutEvents, LayoutCallback } from "./types";
import { Cell } from "./Cell";
import { create } from "@dhx/ts-common/dom";

export class Layout extends Cell implements ILayout {
	public config: ILayoutConfig;

	protected _all;
	protected _cells: ICell[];
	protected _root: ILayout;

	private _xLayout: boolean;
	private _isViewLayout: boolean;

	constructor(parent: any, config: ILayoutConfig) {
		super(parent, config);
		// root layout
		this._root = this.config.parent || this;
		this._all = {};
		this._parseConfig();

		if (this.config.activeTab) {
			this.config.activeView = this.config.activeTab;
		}
		// Need replace to tabbar
		if (this.config.views) {
			this.config.activeView = this.config.activeView || this._cells[0].id;
			this._isViewLayout = true;
		}
		if (!config.parent) {
			const view = create({ render: () => this.toVDOM() }, this);
			this.mount(parent, view);
		}
	}

	destructor(): void {
		for (const key of this._all) this._all[key].destructor();

		this.config = this._cells = this._root = this._xLayout = this._isViewLayout = null;
		this._all = {};
		this.unmount();
	}

	toVDOM() {
		if (this._isViewLayout) {
			const roots = [this.getCell(this.config.activeView).toVDOM()];
			return super.toVDOM(roots);
		}
		let nodes = [];
		this._inheritTypes();
		this._cells.forEach(cell => {
			const node = cell.toVDOM();
			if (Array.isArray(node)) {
				nodes = nodes.concat(node);
			} else {
				nodes.push(node);
			}
		});
		return super.toVDOM(nodes);
	}
	removeCell(id: string) {
		if (!this.events.fire(LayoutEvents.beforeRemove, [id])) {
			return;
		}
		const root = this.config.parent || this;
		if (root !== this) {
			return root.removeCell(id);
		}
		// this === root layout
		const view = this.getCell(id);
		if (view) {
			const parent = view.getParent();
			delete this._all[id];
			parent._cells = parent._cells.filter((cell: ICell) => cell.id != id);
			parent.paint();
		}
		this.events.fire(LayoutEvents.afterRemove, [id]);
	}
	addCell(config: ICellConfig, index = -1) {
		if (!this.events.fire(LayoutEvents.beforeAdd, [config.id])) {
			return;
		}
		const view = this._createCell(config);
		if (index < 0) {
			index = this._cells.length + index + 1;
		}
		this._cells.splice(index, 0, view);
		this.paint();
		if (!this.events.fire(LayoutEvents.afterAdd, [config.id])) {
			return;
		}
	}
	getId(index: number): string {
		if (index < 0) {
			index = this._cells.length + index;
		}
		return this._cells[index] ? this._cells[index].id : undefined;
	}
	getRefs(name: string) {
		return this._root.getRootView().refs[name];
	}
	getCell(id: string) {
		return (this._root as any)?._all[id];
	}
	forEach(callback: LayoutCallback, parent?: string, level = Infinity): void {
		if (!this._haveCells(parent) || level < 1) {
			return;
		}
		let array;
		if (parent) {
			array = (this._root as any)._all[parent]._cells;
		} else {
			array = (this._root as any)._cells;
		}
		for (let index = 0; index < array.length; index++) {
			const cell = array[index];
			callback.call(this, cell, index, array);
			if (this._haveCells(cell.id)) {
				cell.forEach(callback, cell.id, --level);
			}
		}
	}
	/** @deprecated See a documentation: https://docs.dhtmlx.com/ */
	cell(id: string) {
		return this.getCell(id);
	}

	protected _getCss(content?: boolean) {
		const layoutCss = this._xLayout ? "dhx_layout-columns" : "dhx_layout-rows";
		const directionCss = this.config.align ? " " + layoutCss + "--" + this.config.align : "";
		if (content) {
			return (
				layoutCss +
				" dhx_layout-cell" +
				(this.config.align ? " dhx_layout-cell--" + this.config.align : "")
			);
		} else {
			const cellCss = this.config.parent ? super._getCss() : "dhx_widget dhx_layout";
			const fullModeCss = this.config.parent ? "" : " dhx_layout-cell";
			return cellCss + (this.config.full ? fullModeCss : " " + layoutCss) + directionCss;
		}
	}
	private _parseConfig() {
		const config = this.config;
		const cells = config.rows || config.cols || config.views || [];

		this._xLayout = !config.rows;
		this._cells = cells.map(a => this._createCell(a));
	}
	protected _createCell(cell: ILayoutConfig): ICell {
		let view: ICell;
		if (cell.rows || cell.cols || cell.views) {
			cell.parent = this._root;
			view = new Layout(this, cell);
		} else {
			view = new Cell(this, cell);
		}

		// FIxME
		(this._root as any)._all[view.id] = view;
		if (cell.init) {
			cell.init(view, cell);
		}
		return view;
	}
	private _haveCells(id?: string) {
		if (id) {
			const array = (this._root as any)._all[id];
			return array._cells && array._cells.length > 0;
		}
		return Object.keys(this._all).length > 0;
	}

	private _inheritTypes(obj: ICell[] | ICell = this._cells) {
		if (Array.isArray(obj)) {
			obj.forEach(cell => this._inheritTypes(cell));
		} else {
			const cellConfig = obj.config as ILayoutConfig;
			if (cellConfig.rows || cellConfig.cols) {
				const viewParent = obj.getParent();
				if (!cellConfig.type && viewParent) {
					if (viewParent.config.type) {
						cellConfig.type = viewParent.config.type;
					} else {
						this._inheritTypes(viewParent);
					}
				}
			}
		}
	}
}
